import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import { Typography } from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import DialogActions from '@mui/material/DialogActions';
import { LoadingButton } from '@mui/lab';

import { IconX } from '@tabler/icons-react';

import { Button, IconButton } from '@shared/components';
import { FormInput } from '@components/FormInput/ui/FormInput';
import { inviteUserBecomeCompanyMember } from '@api/user/apiService';
import {
  InviteUserFormFields,
  inviteUserFormValidationSchema,
} from '@features/settings-users/utils/invite-user-form-validator';
import useToast from '@hooks/useToast';

interface InviteUserBecomeMemberProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function InviteUserBecomeMemberModal(props: InviteUserBecomeMemberProps) {
  const { open, onClose, onSuccess } = props;
  const [isLoading, setIsLoading] = useState(false);
  const { showSuccess, showError } = useToast();

  const methods = useForm<InviteUserFormFields>({
    resolver: yupResolver(inviteUserFormValidationSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
  } = methods;

  const onSubmit = async (data: InviteUserFormFields) => {
    const errorMessages: string[] = [];

    if (!getValues('firstName')) {
      errorMessages.push('The first name field cannot be empty');
    }
    if (!getValues('lastName')) {
      errorMessages.push('The last name field cannot be empty');
    }
    const email = getValues('email');

    const emailPattern = /^[\w.-]+(\+[\w.-]+)?@[a-zA-Z0-9-]+\.[a-zA-Z]{2,4}$/;

    if (!email || email === '') {
      errorMessages.push('Email is required');
    } else if (!emailPattern.test(email)) {
      errorMessages.push('Invalid email format');
    }

    if (errorMessages.length >= 1) {
      showError('Error', errorMessages.join('\n'));
      return;
    }
    setIsLoading(true);
    inviteUserBecomeCompanyMember(
      data,
      () => {
        showSuccess('Success', 'Invitation was sent successfully');
        setIsLoading(false);
        reset();
        onSuccess();
      },
      (error) => {
        setIsLoading(false);
        showError(
          'Error',
          error.response?.data?.message ?? 'Something went wrong'
        );
      }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <FormProvider {...methods}>
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h3">Invite User</Typography>
            </Box>
            <IconButton onClick={onClose}>
              <IconX />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="center"
            >
              <Grid item xs={12}>
                <FormInput
                  bordered
                  required
                  labelText="First Name"
                  errorText={errors.firstName?.message}
                  type="input"
                  placeholder="Enter first fame"
                  {...register('firstName')}
                />
              </Grid>
              <Grid item xs={12}>
                <FormInput
                  bordered
                  required
                  labelText="Last Name"
                  type="input"
                  errorText={errors.lastName?.message}
                  placeholder="Enter last name"
                  {...register('lastName')}
                />
              </Grid>
              <Grid item xs={12}>
                <FormInput
                  bordered
                  required
                  labelText="Email"
                  type="input"
                  errorText={errors.email?.message}
                  placeholder="Enter email"
                  {...register('email')}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Box
            paddingX={3}
            display="flex"
            justifyContent="space-between"
            gap={2}
          >
            <Button variant="text" onClick={onClose}>
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handleSubmit(onSubmit)}
              loading={isLoading}
            >
              Invite
            </LoadingButton>
          </Box>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

export default InviteUserBecomeMemberModal;
